import { TagRef, TagSuggestionWithOrigin } from '@/entities/tags/utils/types'
import { isSameTag } from '@/entities/tags/utils/helpers'

import { TagSuggestionResponse } from './types'

/**
 * Reads one answer, and refuses anything it cannot trust.
 *
 * The list sibling of `readSuggestion` and `readTypeSuggestion`, with two jobs
 * they do not have:
 *
 *   - it decides which suggestions are **new pairs**, by matching each against
 *     the tags the repository has. The model is never asked and never believed
 *     on that point: what exists is a fact the caller holds.
 *   - it drops anything already applied. The assistant is told not to repeat
 *     those, but the user may have accepted one between the question and the
 *     answer, and a suggestion to apply what is already applied is noise.
 *
 * A suggestion missing a dimension, a value or its grounds is dropped rather
 * than shown — a chip the user cannot judge is worse than one fewer chip — and
 * dropping it is reported so it is not silent.
 */
export const readTagSuggestions = (
  data: string,
  existing: TagRef[],
  applied: TagRef[],
): { suggestions: TagSuggestionWithOrigin[], errors: string[] } => {
  const errors: string[] = []

  let answer: TagSuggestionResponse

  try {
    answer = JSON.parse(data) as TagSuggestionResponse
  } catch {
    return { suggestions: [], errors: ['The tag suggestions were not readable JSON'] }
  }

  if (!answer || !Array.isArray(answer.suggestions)) {
    return { suggestions: [], errors: ['The tag suggestions were not a list'] }
  }

  const suggestions: TagSuggestionWithOrigin[] = []

  for (const item of answer.suggestions) {
    const tagName = item?.tag_name?.trim()
    const tagValue = item?.tag_value?.trim()
    const reason = item?.reason?.trim()

    if (!tagName || !tagValue) {
      errors.push('A tag suggestion arrived without a dimension or a value')
      continue
    }

    if (!reason) {
      errors.push(`The tag suggestion "${tagName} · ${tagValue}" carried no reason`)
      continue
    }

    const pair: TagRef = { tag_name: tagName, tag_value: tagValue }

    // Already on the asset: nothing to suggest.
    if (applied.some(tag => isSameTag(tag, pair))) continue

    // Already suggested in the same answer. The model is told not to, and the
    // backend refuses exact repeats, but this is cheap and final.
    if (suggestions.some(tag => isSameTag(tag, pair))) continue

    suggestions.push({
      ...pair,
      reason,
      isNew: !existing.some(tag => isSameTag(tag, pair)),
    })
  }

  return { suggestions, errors }
}
